import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Panel } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { LogOut, Printer, ShoppingBag } from 'lucide-react';

function Address({ a }) {
  if (!a) return null;
  return <div>{[a.line1, a.line2, a.suburb, a.city, a.region, a.postcode, a.country].filter(Boolean).join(', ')}</div>;
}

function Receipt({ order, onClose }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="max-w-2xl mx-auto my-10 rounded-2xl overflow-hidden" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()}>
        <div className="px-8 py-6 print:px-0">
          <div className="flex items-start justify-between print:hidden">
            <div><div className="h-eyebrow">Receipt</div><div className="h-display text-2xl" style={{ color: 'var(--heritage-plum)' }}>{order.invoice_number}</div></div>
            <div className="flex gap-2"><button onClick={() => window.print()} className="h-btn text-xs"><Printer size={12} className="mr-1" /> Print / save PDF</button>
              <button onClick={onClose} className="h-btn-outline text-xs">Close</button></div>
          </div>
          <div className="mt-6" style={{ color: 'var(--heritage-plum)' }}>
            <div className="text-center"><DahliaMark size={40} className="mx-auto" /><div className="h-serif text-lg tracking-[0.28em] mt-2">HERITAGE APPAREL</div><div className="text-[11px] opacity-75 tracking-widest uppercase">Aotearoa</div></div>
            <div className="h-divider my-4" />
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div><div className="h-eyebrow text-[10px] mb-1">Order</div><div>{order.invoice_number}</div><div className="opacity-75">{new Date(order.created_at).toLocaleString('en-NZ')}</div></div>
              <div><div className="h-eyebrow text-[10px] mb-1">Bill to</div><div>{order.customer_name}</div><div className="opacity-75">{order.customer_email}</div></div>
              <div className="md:col-span-2"><div className="h-eyebrow text-[10px] mb-1">Ship to</div><Address a={order.shipping_address} /></div>
            </div>
            <table className="w-full mt-4 text-sm">
              <thead><tr className="h-eyebrow text-[10px]"><th className="text-left py-2 border-b" style={{ borderColor: 'rgba(75,24,56,0.3)' }}>ITEM</th><th className="text-center border-b" style={{ borderColor: 'rgba(75,24,56,0.3)' }}>QTY</th><th className="text-right border-b" style={{ borderColor: 'rgba(75,24,56,0.3)' }}>AMOUNT</th></tr></thead>
              <tbody>{(order.items || []).map((li) => (
                <tr key={li.sku} className="border-b" style={{ borderColor: 'rgba(75,24,56,0.1)' }}>
                  <td className="py-2">{li.name}<div className="text-[11px] opacity-75">{[li.size && `Size ${li.size}`, li.colour].filter(Boolean).join(' · ')}</div></td>
                  <td className="text-center">{li.qty}</td>
                  <td className="text-right">${Number(li.line_total).toFixed(2)}</td>
                </tr>
              ))}</tbody>
              <tfoot className="text-sm">
                <tr><td colSpan={2} className="pt-3 text-right">Subtotal</td><td className="pt-3 text-right">${Number(order.subtotal || 0).toFixed(2)}</td></tr>
                {order.discount_amount ? <tr><td colSpan={2} className="text-right" style={{ color: '#5B6E2C' }}>Discount ({order.discount_code})</td><td className="text-right" style={{ color: '#5B6E2C' }}>− ${Number(order.discount_amount).toFixed(2)}</td></tr> : null}
                <tr><td colSpan={2} className="text-right">Shipping ({order.shipping_method_name || ''})</td><td className="text-right">${Number(order.shipping || 0).toFixed(2)}</td></tr>
                <tr className="h-serif text-lg"><td colSpan={2} className="pt-2 text-right">Total</td><td className="pt-2 text-right">${Number(order.total).toFixed(2)} {order.currency || 'NZD'}</td></tr>
              </tfoot>
            </table>
            <div className="mt-6 text-[11px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>Thank you for wearing Heritage. Every stitch was made in Aotearoa.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomerConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/console/customer/orders'); setOrders(data.orders || []); }
      catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const doLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen" style={{ background: 'var(--heritage-cream)' }}>
      <header className="border-b" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(246,240,228,0.95)' }}>
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><DahliaMark size={44} /><div><div className="h-serif text-lg tracking-[0.28em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</div><div className="h-eyebrow mt-1">Heritage Apparel Club</div></div></Link>
          <button onClick={doLogout} className="h-btn-outline text-xs"><LogOut size={14} className="mr-2" /> Logout</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="customer-console">
        <Panel className="p-6 flex items-center gap-4">
          <HeritageSeal size={56} />
          <div><div className="h-eyebrow">Welcome back</div><div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Kia ora, {user?.name || user?.email}</div>
            <div className="text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>Track your Heritage orders and download receipts here.</div></div>
        </Panel>

        <Panel className="p-4">
          <div className="h-serif text-xl mb-3" style={{ color: 'var(--heritage-plum)' }}>Your orders</div>
          {loading && <div className="opacity-70" style={{ color: 'var(--heritage-plum)' }}>Loading…</div>}
          {!loading && orders.length === 0 && (
            <div className="text-center py-8">
              <ShoppingBag size={30} className="mx-auto opacity-40" style={{ color: 'var(--heritage-plum)' }} />
              <div className="h-serif text-lg mt-2" style={{ color: 'var(--heritage-plum)' }}>No orders yet</div>
              <Link to="/store" className="h-btn mt-4 inline-flex text-xs">Shop the store</Link>
            </div>
          )}
          <ul className="divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
            {orders.map((o) => (
              <li key={o.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`cust-order-${o.invoice_number}`}>
                <div><div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{new Date(o.created_at).toLocaleString('en-NZ')} · {(o.items || []).length} items · <b>{(o.status || '').toUpperCase()}</b></div></div>
                <div className="flex items-center gap-2">
                  <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>${Number(o.total).toFixed(2)}</div>
                  <button onClick={() => setView(o)} className="h-btn-outline text-xs" data-testid={`cust-receipt-${o.invoice_number}`}><Printer size={12} className="mr-1" /> Receipt</button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {view && <Receipt order={view} onClose={() => setView(null)} />}
    </div>
  );
}
